export type TypeParameterValue = string | boolean | (string | boolean)[];
export type TypeParameterMap = Record<string, TypeParameterValue>;

// ----------------------------------------------------------------------------

function normalizeValue(value: string | boolean, toLowerCase: boolean): string | boolean {
    if (typeof value !== 'string') {
        return value;
    }

    return toLowerCase ? value.toLowerCase() : value;
}

function extractQueryString(url?: string): string {
    if (typeof url === 'string' && url !== '') {
        return url.split('?')[1] ?? '';
    }

    return window.location.search.slice(1);
}

function getArrayKey(paramName: string): string {
    return paramName.replace(/\[(\d+)?\]/, '');
}

function getArrayIndex(paramName: string): number | null {
    const match = /\[(\d+)\]/.exec(paramName);

    return match == null ? null : Number(match[1]);
}

// ----------------------------------------------------------------------------

function get(url?: string, toLowerCase: boolean = false): TypeParameterMap {
    let queryString = extractQueryString(url);
    const obj: TypeParameterMap = {};

    if (queryString === '') {
        return obj;
    }

    // Stuff after # is not part of query string, so get rid of it
    queryString = queryString.split('#')[0];

    for (const item of queryString.split('&')) {
        if (item === '') {
            continue;
        }

        const [rawName, rawValue] = item.split('=');
        const paramName = toLowerCase ? rawName.toLowerCase() : rawName;
        const paramValue = normalizeValue(rawValue === undefined ? true : rawValue, toLowerCase);

        // If the paramName ends with square brackets, e.g. colors[] or colors[2]
        if (/\[(\d+)?\]$/.test(paramName)) {
            const key = getArrayKey(paramName);
            const currentValue = obj[key];
            const arrayValue = Array.isArray(currentValue) ? currentValue : [];

            if (!Array.isArray(currentValue)) {
                obj[key] = arrayValue;
            }

            // If it's an indexed array e.g. colors[2]
            if (/\[\d+\]$/.test(paramName)) {
                const index = getArrayIndex(paramName);

                if (index != null) {
                    arrayValue[index] = paramValue;
                }
            } else {
                // Otherwise add the value to the end of the array
                arrayValue.push(paramValue);
            }

            continue;
        }

        // We're dealing with a scalar key
        if (!(paramName in obj)) {
            obj[paramName] = paramValue;
            continue;
        }

        const currentValue = obj[paramName];

        if (Array.isArray(currentValue)) {
            currentValue.push(paramValue);
            continue;
        }

        obj[paramName] = [currentValue, paramValue];
    }

    return obj;
}

function set(data: Record<string, any>): string {
    const params = new URLSearchParams();

    for (const key in data) {
        const value = data[key];

        params.append(key, String(value ?? ''));
    }

    return params.toString();
}

// ----------------------------------------------------------------------------

export default { get, set };
